import { useEffect, useMemo, useState } from "react";
import {
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { Add, Close, Search } from "@mui/icons-material";
import { nanoid } from "nanoid";

const useStyles = makeStyles((theme) => ({
    titleBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2, 3, 0),
    },
    title: {
        fontFamily: ["Sailec", "Arial", "sans-serif"].join(","),
        fontWeight: 700,
    },
    search: {
        margin: theme.spacing(2, 0),
    },
    layout: {
        display: "grid",
        gap: theme.spacing(3),
        gridTemplateColumns: "1fr",
        [theme.breakpoints.up("md")]: {
            gridTemplateColumns: "1fr 280px",
        },
    },
    checkLabel: {
        alignItems: "flex-start",
        marginLeft: 0,
        "& .MuiFormControlLabel-label": {
            fontSize: "0.9rem",
            paddingTop: 6,
        },
    },
    centerScroll: {
        maxHeight: 360,
        overflowY: "auto",
        paddingRight: theme.spacing(1),
    },
    selectedPanel: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
    },
    selectedTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(1.5),
    },
    selectedList: {
        flex: 1,
        maxHeight: 300,
        overflowY: "auto",
        marginBottom: theme.spacing(2),
    },
    selectedItem: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 0),
    },
    selectedName: {
        flex: 1,
        fontSize: "0.85rem",
    },
    emptySelected: {
        color: theme.palette.text.secondary,
        fontSize: "0.85rem",
    },
    customRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginTop: "auto",
    },
    empty: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(3),
        textAlign: "center",
    },
}));

type PersonLike = { id?: any; name: string };
type CustomPerson = { id: any; name: string; siteExternalId?: string };

interface PeoplePickerDialogProps {
    open: boolean;
    onClose: () => void;
    people: PersonLike[];
    selectedPeopleIds: any[];
    customPeople: CustomPerson[];
    siteExternalId?: string;
    onSave: (result: { peopleIds: any[]; customPeople: CustomPerson[] }) => void;
}

export default function PeoplePickerDialog({
    open,
    onClose,
    people,
    selectedPeopleIds,
    customPeople,
    siteExternalId = "",
    onSave,
}: PeoplePickerDialogProps) {
    const classes = useStyles();

    const [draftIds, setDraftIds] = useState<any[]>([]);
    const [draftCustom, setDraftCustom] = useState<CustomPerson[]>([]);
    const [search, setSearch] = useState("");
    const [newCustomName, setNewCustomName] = useState("");

    useEffect(() => {
        if (open) {
            setDraftIds([...(selectedPeopleIds ?? [])]);
            setDraftCustom([...(customPeople ?? [])]);
            setSearch("");
            setNewCustomName("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const allPeople = people ?? [];
    const trimmedSearch = search.trim().toLowerCase();

    const visiblePeople = useMemo(
        () =>
            trimmedSearch
                ? allPeople.filter((p) => p.name.toLowerCase().includes(trimmedSearch))
                : allPeople,
        [allPeople, trimmedSearch]
    );

    function togglePerson(id: any) {
        setDraftIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
    }

    function addCustomPerson() {
        const name = newCustomName.trim();
        if (!name) return;
        const exists =
            draftCustom.some((c) => c.name.trim().toLowerCase() === name.toLowerCase()) ||
            allPeople.some((p) => p.name.trim().toLowerCase() === name.toLowerCase());
        if (exists) return;
        setDraftCustom((customs) => [...customs, { id: nanoid(5), name, siteExternalId }]);
        setNewCustomName("");
    }

    function removeCustomPerson(id: any) {
        setDraftCustom((customs) => customs.filter((c) => c.id !== id));
    }

    const selectedPeople = allPeople.filter((p) => draftIds.includes(p.id));
    const totalSelected = selectedPeople.length + draftCustom.length;

    function handleSave() {
        onSave({ peopleIds: draftIds, customPeople: draftCustom });
        onClose();
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="people-picker-title">
            <div className={classes.titleBar}>
                <Typography variant="h5" className={classes.title} id="people-picker-title">
                    Add People at Risk
                </Typography>
                <IconButton onClick={onClose} aria-label="Close">
                    <Close />
                </IconButton>
            </div>
            <DialogContent>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search people"
                    className={classes.search}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                <div className={classes.layout}>
                    <div>
                        {visiblePeople.length === 0 ? (
                            <Typography className={classes.empty}>No people match your search.</Typography>
                        ) : (
                            <div className={classes.centerScroll}>
                                <Grid container spacing={0.5}>
                                    {visiblePeople.map((person) => (
                                        <Grid size={{ xs: 12, sm: 6 }} key={person.id}>
                                            <FormControlLabel
                                                className={classes.checkLabel}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={draftIds.includes(person.id)}
                                                        onChange={() => togglePerson(person.id)}
                                                    />
                                                }
                                                label={person.name}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                        )}
                    </div>

                    <div className={classes.selectedPanel}>
                        <Typography variant="subtitle1" className={classes.selectedTitle}>
                            Selected People {totalSelected > 0 ? `(${totalSelected})` : ""}
                        </Typography>
                        <div className={classes.selectedList}>
                            {totalSelected === 0 ? (
                                <Typography className={classes.emptySelected}>No people selected yet.</Typography>
                            ) : (
                                <>
                                    {selectedPeople.map((person) => (
                                        <div className={classes.selectedItem} key={`p-${person.id}`}>
                                            <Typography className={classes.selectedName}>{person.name}</Typography>
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${person.name}`}
                                                onClick={() => togglePerson(person.id)}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </div>
                                    ))}
                                    {draftCustom.map((custom) => (
                                        <div className={classes.selectedItem} key={`c-${custom.id}`}>
                                            <Typography className={classes.selectedName}>{custom.name}</Typography>
                                            <Chip size="small" label="Custom" color="secondary" variant="outlined" />
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${custom.name}`}
                                                onClick={() => removeCustomPerson(custom.id)}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                        <div className={classes.customRow}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder="Add custom person"
                                value={newCustomName}
                                onChange={(e) => setNewCustomName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addCustomPerson();
                                    }
                                }}
                            />
                            <Button
                                color="secondary"
                                startIcon={<Add />}
                                disabled={!newCustomName.trim()}
                                onClick={addCustomPerson}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions sx={{ padding: 2 }}>
                <Button variant="outlined" color="primary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
